import { useCallback, useEffect, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { ELIG_STATUS } from "./EligibilityPanel";

export const EligibilityOverview = () => {
  const [rows, setRows] = useState([]);
  const [runningId, setRunningId] = useState(null);

  const load = useCallback(() => api.get("/eligibility/all").then((res) => setRows(res.data)), []);
  useEffect(() => {
    load();
  }, [load]);

  const run = async (familyId) => {
    setRunningId(familyId);
    try {
      await api.post("/eligibility/evaluate", null, { params: { family_id: familyId } });
      toast.success("Eligibility check complete");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Eligibility check failed");
    } finally {
      setRunningId(null);
    }
  };

  if (rows.length === 0) return null;

  return (
    <section data-testid="eligibility-overview" className="fade-in-up">
      <h2 className="text-lg font-heading font-semibold mb-4">Family eligibility overview</h2>
      <div className="bg-white border border-[#E8E6E1] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E8E6E1] bg-[#F9F7F4]">
              <th className="text-left px-5 py-3 text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A]">Family</th>
              <th className="text-left px-5 py-3 text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A]">Status</th>
              <th className="text-left px-5 py-3 text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A] hidden md:table-cell">Likelihood</th>
              <th className="text-left px-5 py-3 text-xs uppercase tracking-[0.15em] font-semibold text-[#57605A] hidden md:table-cell">Last checked</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const status = row.report ? ELIG_STATUS[row.report.eligibility_status] : null;
              return (
                <tr key={row.family_id} data-testid={`eligibility-row-${row.family_id}`} className="border-b border-[#E8E6E1] last:border-0 hover:bg-[#F9F7F4]/60 transition-colors duration-150">
                  <td className="px-5 py-3 font-medium text-[#1C211F]">{row.family_name}</td>
                  <td className="px-5 py-3">
                    {status ? (
                      <span className={`${status.cls} px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider`}>
                        {status.label}
                      </span>
                    ) : (
                      <span className="text-xs text-[#57605A]">Not evaluated</span>
                    )}
                  </td>
                  <td className="px-5 py-3 hidden md:table-cell capitalize text-[#57605A]">
                    {row.report?.approval_likelihood || "—"}
                  </td>
                  <td className="px-5 py-3 hidden md:table-cell text-[#57605A]">
                    {row.checked_at ? new Date(row.checked_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      data-testid={`run-eligibility-${row.family_id}`}
                      onClick={() => run(row.family_id)}
                      disabled={runningId !== null}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#2E5C50] hover:text-[#22463D] transition-colors duration-200 disabled:opacity-50"
                    >
                      {runningId === row.family_id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />
                      ) : (
                        <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />
                      )}
                      Run check
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};
